/// <amd-module name="VSS/Features/Charts/Internal/CommonChartImplementation" />

import React = require("react");
import Highcharts = require("highcharts");
import { IVssThemeService } from "VSS/Platform/Theming";
import { isHighContrastMode } from "./CompatUtilities";
import * as Charts_Contracts from "../Contracts";
import { TruncationFormatter, AxisMonthFormatter, AbbreviatedNumberFormatter, TimeSpanFormatter, PercentageNumberFormatter } from "./AxisLabelFormatter";
import { AxisTickPositioner } from "./AxisTickPositioner";
import { DefaultColorDictionary } from "./DefaultColorDictionary";
import { ColorUtilities } from "./ColorUtilities";
import * as StringUtilities from "./StringUtilities";
import { LegendLabelFormatter } from "./LegendLabelFormatter";
import { TooltipFormatter, TooltipFormatterOptions, ITooltipFormattingSupportedChart, TooltipFormatterPoint } from "./TooltipFormatter";
import { TabularComponent } from "./TabularComponent";
import { translateAccessibilityOptions } from "./AccessibilityUtilities";
import { VssChartsSeriesOptions, HighchartsExtendedPointObject } from "./InternalContracts";

/**
 * Encapsulates Implementation details of Chart, with two major responsibilities:
 * 1-option transformation into highcharts
 * 2-interaction handling with consumer
 *
 * Charts with unusual rendering behavior must also handle rendering of Accessible table.
 */
export class CommonChartImplementation implements ITooltipFormattingSupportedChart {
    // (Deprecated pattern) This should only be used for Click event handlers on a rendered chart. Chart transforms should be pure functions.
    protected _chartOptions: Charts_Contracts.ChartProps;
    protected _injectAxisNames: boolean;

    public static readonly LabelFonts = "'Segoe UI', 'Helvetica Neue', Helvetica, Arial, Verdana";

    //Note: SVG is literal. It does not propagate high contrast coloring from OS.
    //So we use system styles, which are only recognized by IE and Edge, to use the OS colors.
    public static readonly HighContrastForeground = "WindowText";
    public static readonly HighContrastLegendTextColor = "ButtonText";
    public static readonly HighContrastLegendTextHoverColor = "ButtonHighlight";
    public static readonly HighContrastTooltipBackgroundColor = "Background";

    /** The font size used for the axis titles */
    public static readonly DefaultAxisTitleFontSizePx = 12;
    public static readonly DefaultAxisLineWidth = 1;
    public static readonly ChartRenderContainerClass = "chart-render-container";

    constructor() {
        this._injectAxisNames = false;
    }

    /** Default Implementation for rendering chart options in a textual-tabular representation for screen readers.*/
    public getTabularComponent(chartOptions: Charts_Contracts.ChartProps): JSX.Element {
        return React.createElement(TabularComponent, chartOptions);
    }

    /**
     * Entry point for translating chart options to highcharts options.
     * @param chartOptions
     */
    public translateOptions(chartOptions: Charts_Contracts.ChartProps): Highcharts.Options {
        this._chartOptions = chartOptions;
        return this.translateOptionsImpl(chartOptions);
    }

    public getColoringNames(chartOptions: Charts_Contracts.ChartProps): string[] {
        var names;
        if (this.coloredBySeries()) {
            names = chartOptions.series.map((value, i) => {
                return value.name;
            });
        } else {
            if (chartOptions.xAxis && chartOptions.xAxis.labelValues) {
                names = chartOptions.xAxis.labelValues;
            } else {
                names = [];
            }
        }
        return names;
    }

    /* Option translators Implementations */

    /**
     * This implementation method is purely for internal convenience to assist in UT sanity.
     * @param chartOptions
     */
    public translateOptionsImpl(chartOptions: Charts_Contracts.ChartProps): Highcharts.Options {
        var colorizer = new ChartColorizer(chartOptions.colorCustomizationOptions);

        var highchartsOptions: Highcharts.Options = {
            chart: this.translateChartOptions(chartOptions),
            accessibility: translateAccessibilityOptions(chartOptions),
            title: { text: "" },
            credits: { enabled: false },
            exporting: { enabled: false },
            legend: this.translateLegendOptions(chartOptions, chartOptions.legend),
            xAxis: this.translateXAxisArrayOptions([chartOptions.xAxis]) as any,
            yAxis: this.translateYAxisArrayOptions([chartOptions.yAxis, chartOptions.yAxisSecondary]) as any,

            series: this.translateSeriesOptions(chartOptions, chartOptions.series, chartOptions.suppressAnimation, chartOptions.chartType, colorizer) as any,
            plotOptions: this.translatePlotOptions(chartOptions),
            tooltip: this.translateTooltipOptions(chartOptions.tooltip)
        };

        let cursor = chartOptions.click != null ? "pointer" : undefined;
        highchartsOptions.plotOptions.series = {
            cursor: cursor,
            events: {
                click: (event: Highcharts.SeriesClickEventObject) => {

                    if (event.point.series?.userOptions?.name !== 'Total') {
                        (event.point.series?.userOptions?.name === '(other)' || 
                         event.point.category === "(other)" || 
                         event.point.options.name === "(other)" || 
                         (event.point.series?.yAxis?.categories && event.point.series?.yAxis?.categories[event.point.y] === '(other)')) ? this.onMouseClick(event, true) : this.onMouseClick(event);
                    }
                },
                // NOTE: This event does not fire for pie charts since in pies legend items are per-point rather than per-series.
                // See PieChartImplementation for its legendItemClick event.
                legendItemClick: (event: Highcharts.SeriesLegendItemClickEventObject) => {
                    return this.onLegendClick(event);
                }
            },
            enableMouseTracking: !chartOptions.tooltip || chartOptions.tooltip.enabled !== false,
            point: {
                events: {
                    click: (event: Highcharts.PointClickEventObject) => {
                        // Mouse events are already explicitly handled on the series layer.
                        // This layer is strictly responsible for propagating keyboard accessibility events, which are not exposed via series events.
                        if (event.constructor.name !== "MouseEvent") {
                            this.onKeyClick(event);
                        }
                    }
                }
            },
            states: {
                inactive: { opacity: 1 }, // When hovering over a series or its legend, prevent dimming of other series
            }
        };

        // If we have an annotation line then we want to increase the top margin to draw the annotation message. if we dont specify, Highcharts will default to 10.
        if (chartOptions.xAxis && chartOptions.xAxis.plotLines != null) {
            highchartsOptions.chart.spacingTop = 15;
        }
        return highchartsOptions;
    }

    public translateChartOptions(chartOptions: Charts_Contracts.ChartProps): Highcharts.ChartOptions {
        const classNames: string[] = [CommonChartImplementation.ChartRenderContainerClass];
        var highChartOptions = <Highcharts.ChartOptions>{
            animation: this.translateAnimationOptions(chartOptions.suppressAnimation),
            style: {
                fontFamily: CommonChartImplementation.LabelFonts
            }
        };

        if (chartOptions.suppressMargin === true) {
            // Axis lines have a width of 1 , so margin needs to be atleast 1 if we want to show axis lines
            const bottomMargin = chartOptions.xAxis && chartOptions.xAxis.retainAxisLine ? CommonChartImplementation.DefaultAxisLineWidth : 0;
            const leftMargin = chartOptions.yAxis && chartOptions.yAxis.retainAxisLine ? CommonChartImplementation.DefaultAxisLineWidth : 0;
            highChartOptions.margin = [0, 0, bottomMargin, leftMargin];
        }
        if (chartOptions.legend && chartOptions.legend.limitLabelSize) {
            classNames.push("limited-label-chart");
        }
        if (chartOptions.hostOptions) {
            highChartOptions.height = chartOptions.hostOptions.height;
            highChartOptions.width = chartOptions.hostOptions.width;
        }

        var zoomType = this.getAxisZoomType(chartOptions);
        if (zoomType !== undefined) {
            highChartOptions.zoomType = zoomType;
        }

        highChartOptions.backgroundColor = "transparent";

        highChartOptions.className = classNames.join(" ");

        return highChartOptions;
    }

    public translateAnimationOptions(suppressAnimation: boolean): Highcharts.AnimationOptionsObject | boolean {
        var result: boolean | Highcharts.AnimationOptionsObject;
        if (suppressAnimation === true) {
            result = false;
        } else {
            result = {
                duration: 200,
                easing: "easeOutBounce"
            };
        }

        return result;
    }

    public hasLegendByDefault(): boolean {
        return !this.isOneDimensional();
    }

    public translateLegendOptions(chartOptions: Charts_Contracts.ChartProps, legendOptions: Charts_Contracts.LegendOptions): Highcharts.LegendOptions {
        var isEnabled;
        //Simple charts do not use legend. All others support legend by default
        if (legendOptions == null || legendOptions.enabled == null) {
            isEnabled = this.hasLegendByDefault();
        } else {
            isEnabled = legendOptions.enabled;
        }

        var isHighContrast = isHighContrastMode();

        // Use theme service to set legend color for dark theme compat.
        const themeService = chartOptions.pageContext.getService<IVssThemeService>("IVssThemeService");
        const standardLegendTextColor = themeService ? themeService.getThemeValue("text-primary-color") : "#222222";

        var translatedLegendOptions: Highcharts.LegendOptions;
        if (isEnabled) {
            translatedLegendOptions = <Highcharts.LegendOptions>{
                enabled: true,
                useHTML: false, // When false, SVG occasionally renders blurry sub pixel edges & the text is hard to read in high contrast mode
                layout: "horizontal",
                align: "left",
                alignColumns: false, //column alignment was causing wasted space and overflows because the widest columns were winning
                borderRadius: 0,
                borderWidth: 0,
                verticalAlign: "bottom",
                itemStyle: <Highcharts.CSSObject>{
                    fontFamily: CommonChartImplementation.LabelFonts,
                    fontWeight: "normal",
                    color: isHighContrast ? CommonChartImplementation.HighContrastLegendTextColor : standardLegendTextColor
                },
                itemHoverStyle: <Highcharts.CSSObject>{
                    color: isHighContrast ? CommonChartImplementation.HighContrastLegendTextHoverColor : standardLegendTextColor
                },
                labelFormatter: LegendLabelFormatter.getFormatter(),
                itemDistance: 7,
                symbolWidth: 12, // Same as the default item font size which is what the symbol height uses by default
                symbolPadding: 5,
                symbolRadius: 0 // Render legend items as squares instead of the default of circles
            };

            if (legendOptions) {
                if (legendOptions.rightAlign || legendOptions.stackVertically) {
                    //legacy compat for 'rightAlign' option
                    translatedLegendOptions.layout = "vertical";
                    translatedLegendOptions.align = "right";
                    translatedLegendOptions.verticalAlign = "top";
                } else if (legendOptions.align === "left" || legendOptions.align === "right" || legendOptions.align === "center") {
                    //horizontal stacking options
                    translatedLegendOptions.align = legendOptions.align;
                }

                if (legendOptions.limitLabelSize) {
                    // Tested for support of 3 rows for 9 labels in small form factors (2x2 Widgets)
                    var itemStyle = <any>translatedLegendOptions.itemStyle;
                    itemStyle.textOverflow = "ellipsis";
                    itemStyle.overflow = "hidden";
                    var formatLength = legendOptions.rightAlign ? 16 : TruncationFormatter.MaxLabelLength;

                    translatedLegendOptions.labelFormatter = LegendLabelFormatter.getFormatter(formatLength);
                }
            }
        } else {
            translatedLegendOptions = { enabled: false };
        }

        return translatedLegendOptions;
    }

    /* Expose the neccessary set of plot option defaults.*/
    public translatePlotOptions(chartOptions: Charts_Contracts.ChartProps): Highcharts.PlotOptions {
        //TODO: Factor the type specific parts to derived types.
        const connectNulls = (this._chartOptions.interpolateNullValues !== false); //Connect nulls by default, unless interpolation is explicitly opted out.
        var plotOptions: Highcharts.PlotOptions = {
            series: {},
            area: {
                lineColor: chartOptions.specializedOptions && (<Charts_Contracts.AreaChartOptions>chartOptions.specializedOptions).lineColor,
                trackByArea: true, // handle and track mouse with the plotted area, not just the line edges
                stacking: (chartOptions.specializedOptions && (<Charts_Contracts.AreaChartOptions>chartOptions.specializedOptions).disableStacking) ? undefined : "normal",
                lineWidth: (chartOptions.specializedOptions && (<Charts_Contracts.AreaChartOptions>chartOptions.specializedOptions).lineWidth) || 1,
                fillOpacity: 1,
                marker: {
                    enabled:
                        (<Charts_Contracts.AreaChartOptions>chartOptions.specializedOptions &&
                            (<Charts_Contracts.AreaChartOptions>chartOptions.specializedOptions).includeMarkers === true) ||
                        false,
                    symbol: "circle",
                    radius: 4,
                    lineWidth: 1,
                    states: {
                        hover: {
                            radiusPlus: 0,
                            lineWidthPlus: 0
                        }
                    }
                },
                connectNulls: connectNulls,
                fillColor: chartOptions.specializedOptions && (<Charts_Contracts.AreaChartOptions>chartOptions.specializedOptions).gradientFill && {
                    linearGradient: (<Charts_Contracts.AreaChartOptions>chartOptions.specializedOptions).gradientFill.linearGradient,
                    stops: this._generateStopsforAreaChart((<Charts_Contracts.AreaChartOptions>chartOptions.specializedOptions).gradientFill.stops)
                } as Highcharts.GradientColorObject
            },
            line: {
                marker: {
                    enabled:
                        (<Charts_Contracts.LineChartOptions>chartOptions.specializedOptions &&
                            (<Charts_Contracts.LineChartOptions>chartOptions.specializedOptions).includeMarkers === true) ||
                        false,
                    symbol: "circle",
                    radius: 4
                },
                connectNulls: connectNulls
            },
            bar: {
                stacking: "normal"
            },
            column: {
                stacking: "normal"
            },
            arearange: {
                marker: {
                    enabled: false
                }
            }
        };

        if (chartOptions.chartType === Charts_Contracts.ChartTypesConstants.Histogram) {
            plotOptions.column.pointPadding = 0;
            plotOptions.column.groupPadding = 0.01;
        }
        return plotOptions;
    }

    private _generateStopsforAreaChart(stops: Charts_Contracts.Stops[]): Array<[number, string]> {
        if (stops != null) {
            return stops.map(d => {
                return [d.position, (new Highcharts.Color(d.color)).setOpacity(d.opacity).get('rgba').toString()];
            });
        }
    }

    public translateSeriesOptions(
        chartOptions: Charts_Contracts.ChartProps,
        seriesOptions: Charts_Contracts.DataSeries[],
        suppressAnimation: boolean,
        chartType: string,
        colorizer: any
    ): Highcharts.SeriesOptions[] {
        var mappedOptions: Highcharts.SeriesOptions[] = [];
        if (seriesOptions) {
            for (var i = 0; i < seriesOptions.length; i++) {
                mappedOptions[i] = this.translateSeriesOption(chartOptions, seriesOptions[i], i, suppressAnimation, chartType, <ChartColorizer>colorizer);
            }
        }
        return mappedOptions;
    }

    /**
     * Interpret the series type based on the type of chart and the index of the series.
     * This is used to deal with reconciling stack charts, as well as hybrid implementations (column-line).
     * @param chartType
     * @param index
     */
    public mapSeriesType(chartType: string, index?: number): string {
        return chartType;
    }

    //Encapsulate params here for cleaner testability.
    public translateSeriesOption(
        chartOptions: Charts_Contracts.ChartProps,
        seriesOption: Charts_Contracts.DataSeries,
        seriesIndex: number,
        suppressAnimation: boolean,
        chartType: string,
        colorizer: any
    ): Highcharts.SeriesOptions {
        var mappedOption: VssChartsSeriesOptions = {
            type: this.mapSeriesType(chartType, seriesIndex)
        };
        mappedOption.name = StringUtilities.encodeUntrustedString(seriesOption.name);
        if (seriesOption.stackGroup) {
            mappedOption.stack = seriesOption.stackGroup;
        }

        var typedColorizer: ChartColorizer = <ChartColorizer>colorizer;

        //Map colors
        if (this.coloredBySeries()) {
            if (seriesOption.data == null) {
                mappedOption.data = seriesOption.data;
            } else {
                let filteredData = [];
                seriesOption.data.forEach((val: any) => {
                    if (typeof val === "number") {
                        filteredData.push(val);
                    } else {
                        filteredData.push(this.filterDatum(val));
                    }
                });
                mappedOption.data = filteredData;
            }

            //Apply series-scoped color, if it is already present.
            if (seriesOption.color == null) {
                const colorPair = typedColorizer.getColor(seriesIndex, seriesOption.name);
                mappedOption.color = StringUtilities.encodeUntrustedString(colorPair.background);
                mappedOption.dataLabels = {
                    color: colorPair.foreground
                };
            } else {
                mappedOption.color = StringUtilities.encodeUntrustedString(seriesOption.color);
                mappedOption.dataLabels = {
                    color: ColorUtilities.selectForegroundColor(mappedOption.color)
                };
            }
        } else {
            var dataSet = [];
            var elementLength = seriesOption.data.length;
            for (var j = 0; j < elementLength; j++) {
                //This should be generalized as a hook point for Piecharts
                var elementName = "";
                if (chartOptions.xAxis && chartOptions.xAxis.labelValues) {
                    elementName = chartOptions.xAxis.labelValues[j];
                }

                //Handle datum depending on if they were number or objects
                var preparedDatum: Charts_Contracts.Datum;
                const colorPair = typedColorizer.getColor(j, elementName);
                if (typeof seriesOption.data[j] == "number") {
                    preparedDatum = {
                        y: <number>seriesOption.data[j],
                        color: colorPair.background,
                        dataLabels: {
                            color: colorPair.foreground
                        }
                    };
                } else {
                    preparedDatum = {
                        ...{
                            dataLabels: {
                                color: colorPair.foreground
                            }
                        },
                        ...{
                            color: colorPair.background
                        },
                        ...(seriesOption.data[j] as any)
                    };
                }

                if (this._injectAxisNames) {
                    preparedDatum.name = elementName;
                }
                preparedDatum = this.filterDatum(preparedDatum);

                dataSet.push(preparedDatum);
            }
            mappedOption.data = dataSet;
        }

        if (seriesOption.useSecondaryAxis) {
            mappedOption.yAxis = 1;
        }

        if (seriesOption.markerType) {
            mappedOption.marker = {
                symbol: seriesOption.markerType
            };
        }

        if (seriesOption.markerRadius) {
            if (mappedOption.marker != null) {
                mappedOption.marker.radius = seriesOption.markerRadius;
            } else {
                mappedOption.marker = {
                    radius: seriesOption.markerRadius
                };
            }
        }

        if (seriesOption.enableTooltip != undefined) {
            mappedOption.enableMouseTracking = seriesOption.enableTooltip;
        }

        if (seriesOption.plotDashedStyle) {
            mappedOption.dashStyle = "ShortDash";
        }

        if (suppressAnimation) {
            mappedOption.animation = false;
        }

        // Packed the custom data along with the series
        if (seriesOption.customData != undefined) {
            mappedOption.customData = seriesOption.customData;
        }

        return mappedOption;
    }

    /**
     * Filter Datum/object elements to deal with untrusted object inputs.
     * @param preparedDatum
     */
    private filterDatum(preparedDatum: Charts_Contracts.Datum): Charts_Contracts.Datum {
        if (preparedDatum != null) {
            if (typeof preparedDatum === "object") {
                //For numerical array content, only pass through numerical values
                if (Array.isArray(preparedDatum)) {
                    var valueArray = preparedDatum as number[];
                    preparedDatum = valueArray.map((val: number, i) => {
                        return typeof val === "number" ? val : 0;
                    }) as any;
                } else {
                    if (preparedDatum.name) {
                        preparedDatum.name = StringUtilities.encodeUntrustedString(preparedDatum.name);
                    }
                    if (preparedDatum.color) {
                        preparedDatum.color = StringUtilities.encodeUntrustedString(preparedDatum.color);
                    }
                    if (typeof preparedDatum.y !== "number") {
                        preparedDatum.y = 0;
                    }
                }
            } else {
                // Discard any kind of unrecognized types.
                preparedDatum = {};
            }
        }

        return preparedDatum;
    }

    /**
     *  Generally:
     *  2D Charts should be colored based on series
     *  1D charts should be colored based on individual sample points.
     */
    public coloredBySeries(): boolean {
        return !this.isOneDimensional();
    }

    /* Dimensionality is a key distinguishing factor of the charts.
     * By having charts self describ this aspect, we can skip repetitive details by type.
     * Two key aspects:
     *   -Tooltips
     *   -Visibility of Legend
     */
    public isOneDimensional(): boolean {
        return false;
    }

    public mapDataPointsToTooltipLineItems(points: Charts_Contracts.DataPoint[]): Charts_Contracts.TooltipLineItem[] {
        return points.map(point => {
            let item = {
                styleType: {
                    color: point.color,
                    type: Charts_Contracts.TooltipLineItemMarkerType.Square
                },
                text: ""
            } as Charts_Contracts.TooltipLineItem;
            var localedNumber = Number(point.values[0]).toLocaleString(navigator.language);
            if (this.isOneDimensional()) {
                item.text = localedNumber;
            } else {
                item.text = `${point.seriesName} : ${localedNumber}`;
            }

            return item;
        });
    }

    public getTooltipHeaderValue(formatterPoint: TooltipFormatterPoint): string | number {
        return formatterPoint.point.category;
    }

    public interpretPointAsValuesForTooltip(point: Highcharts.Point): (string | number)[] {
        return [point.y];
    }

    public translateTooltipOptions(tooltipOptions: Charts_Contracts.TooltipOptions): Highcharts.TooltipOptions {
        tooltipOptions = tooltipOptions || {};

        var highchartTooltipOptions: Highcharts.TooltipOptions = {};

        highchartTooltipOptions.enabled = tooltipOptions.enabled != false;

        if (highchartTooltipOptions.enabled) {
            highchartTooltipOptions.shared = tooltipOptions.onlyShowFocusedSeries !== true;

            highchartTooltipOptions.backgroundColor = isHighContrastMode() ? CommonChartImplementation.HighContrastTooltipBackgroundColor : "#FFF";
            highchartTooltipOptions.borderColor = "#ccc";
            highchartTooltipOptions.useHTML = true;

            highchartTooltipOptions.outside = true;
            highchartTooltipOptions.style = {
                fontFamily: CommonChartImplementation.LabelFonts
            };

            var formatterOptions = {
                reverseSeries: tooltipOptions.reverseSeries,
                isOneDimensional: this.isOneDimensional(),
                tooltipMapping: tooltipOptions.customTooltipMapping,
                tooltipHeader: tooltipOptions.customTooltipHeader
            } as TooltipFormatterOptions;

            var defaultFormatter = new TooltipFormatter(formatterOptions);
            highchartTooltipOptions.formatter = defaultFormatter.getFormatter(this);
        }
        return highchartTooltipOptions;
    }

    public translateAxisArrayOptions(axisOptions: Charts_Contracts.AxisOptions[]): Highcharts.AxisOptions[] {
        var highchartsAxisOptions: Highcharts.AxisOptions[] = [];
        for (var i = 0; i < axisOptions.length; i++) {
            if (axisOptions[i] != null || i == 0) {
                highchartsAxisOptions.push(this.translateAxisOptions(axisOptions[i]));
            }
        }

        //Secondary Axis should always be opposite to the main axis.
        if (highchartsAxisOptions.length == 2 && highchartsAxisOptions[1] != null) {
            highchartsAxisOptions[1].opposite = true;
        }

        return highchartsAxisOptions;
    }

    public translateXAxisArrayOptions(axisOptions: Charts_Contracts.AxisOptions[]): Highcharts.AxisOptions[] {
        return this.translateAxisArrayOptions(axisOptions);
    }

    public translateYAxisArrayOptions(axisOptions: Charts_Contracts.AxisOptions[]): Highcharts.AxisOptions[] {
        var translatedOptions = this.translateAxisArrayOptions(axisOptions);

        var secondAxis = translatedOptions[1];
        // Do we have an opposite axis with rotated title?
        if (secondAxis != null && secondAxis.opposite && secondAxis.title && secondAxis.title.rotation === 270) {
            // More space is allocated for the title text than is used by the text of our titles.
            // We only care about the visual perception of space between the title text and the axis labels meaning we want
            // to ignore that extra space when shifting the rotated opposite axis title. To do that we have to account for it
            // in our calculation for how far to shift the title. We approximate how much extra space is allocated
            // as 1/ 3 of the font size.
            var axisTitleXShift = Math.floor((CommonChartImplementation.DefaultAxisTitleFontSizePx * 2) / 3);

            // Because the second axis title was rotated around its text baseline,
            // it ends up being too close to the axis labels.
            // To remedy this we shift the title to the right.
            secondAxis.title.x = axisTitleXShift;
        }

        return translatedOptions;
    }

    public translateAxisOptions(axisOptions: Charts_Contracts.AxisOptions): Highcharts.AxisOptions {
        if (axisOptions == null) {
            axisOptions = {};
        }

        var highchartsAxisOptions: Highcharts.XAxisOptions | Highcharts.YAxisOptions = {};
        var isHighContrast = isHighContrastMode();

        var axisLineColor = isHighContrast ? CommonChartImplementation.HighContrastForeground : "#cccccc";
        var axisLabelsColor = isHighContrast ? CommonChartImplementation.HighContrastForeground : "#767676";

        highchartsAxisOptions.reversedStacks = false; //Build stacks from bottom up

        //Apply Common Options
        if (axisOptions.min != null) {
            highchartsAxisOptions.min = axisOptions.min;
        }
        if (axisOptions.max != null) {
            highchartsAxisOptions.max = axisOptions.max;
        }
        if (axisOptions.softMax != null) {
            highchartsAxisOptions.softMax = axisOptions.softMax;
        }

        if (axisOptions.startOnTick != null) {
            highchartsAxisOptions.startOnTick = axisOptions.startOnTick;
        }

        if (axisOptions.endOnTick != null) {
            highchartsAxisOptions.endOnTick = axisOptions.endOnTick;
        }

        if (axisOptions.enabled != null) {
            highchartsAxisOptions.visible = axisOptions.enabled;
        }

        highchartsAxisOptions.title = {};
        if (axisOptions.title == null) {
            highchartsAxisOptions.title.text = null;
        } else {
            highchartsAxisOptions.title.text = StringUtilities.encodeUntrustedString(axisOptions.title);
            //Note: This should be factored to Y-axis handling.
            highchartsAxisOptions.title.rotation = 270;

            highchartsAxisOptions.title.style = {
                color: axisLabelsColor,
                fontSize: CommonChartImplementation.DefaultAxisTitleFontSizePx + "px",
                fontFamily: CommonChartImplementation.LabelFonts
            };
        }

        highchartsAxisOptions.labels = {};

        //suppress labels strictly if they are opted out.
        if (axisOptions.labelsEnabled === false) {
            highchartsAxisOptions.labels.enabled = false;
        } else {
            highchartsAxisOptions.labels.style = {
                color: axisLabelsColor,
                fontSize: "12px",
                fontFamily: CommonChartImplementation.LabelFonts,
                textoverflow: "none"
            };
        }

        //suppress axis markings if they are opted out.
        if (axisOptions.markingsEnabled === false) {
            highchartsAxisOptions.minorTickLength = 0;
            highchartsAxisOptions.tickLength = 0;
            highchartsAxisOptions.lineColor = axisOptions.retainAxisLine ? axisLineColor : "transparent";
            highchartsAxisOptions.lineWidth = axisOptions.retainAxisLine ? CommonChartImplementation.DefaultAxisLineWidth : 0;
        } else {
            highchartsAxisOptions.lineWidth = CommonChartImplementation.DefaultAxisLineWidth;
            highchartsAxisOptions.tickWidth = 1;
            highchartsAxisOptions.tickLength = 6;
            highchartsAxisOptions.tickColor = axisLineColor;
            highchartsAxisOptions.lineColor = axisLineColor;
        }

        if (!axisOptions.gridLineOptions || !axisOptions.gridLineOptions.enabled) {
            highchartsAxisOptions.gridLineWidth = 0;
        }

        if (axisOptions.gridLineOptions && axisOptions.gridLineOptions.gridLineColor) {
            highchartsAxisOptions.gridLineColor = axisOptions.gridLineOptions.gridLineColor;
        }

        //Hide the axis line and title when axis has no data (e.g. when associated datasets are toggled out of view)
        highchartsAxisOptions.showEmpty = true;

        if (axisOptions.renderToEdges && axisOptions.labelValues) {
            highchartsAxisOptions.min = 0.5; //Remove gap between axis and first samples.
            highchartsAxisOptions.max = Math.max(1, axisOptions.labelValues.length - 1.5); //Remove gap between axis and last samples.
        }

        //Apply Format-specific details
        if (axisOptions.labelFormatMode == Charts_Contracts.LabelFormatModes.Linear) {
            highchartsAxisOptions.type = "linear";
        } else if (axisOptions.labelFormatMode == Charts_Contracts.LabelFormatModes.Percentage) {
            highchartsAxisOptions.type = "linear";
            highchartsAxisOptions.labels.formatter = PercentageNumberFormatter.getFormatter();
        } else if (axisOptions.labelFormatMode == Charts_Contracts.LabelFormatModes.DateTime_DayInMonth) {
            highchartsAxisOptions.type = "datetime";

            if (axisOptions.labelValues) {
                var dates = [];
                for (var i = 0; i < axisOptions.labelValues.length; i++) {
                    var dateValue = axisOptions.labelValues[i];

                    //Roundtrip compat: Gracefully normalize input dates
                    var date: Date;
                    if (dateValue.getDate) {
                        date = dateValue;
                    } else {
                        const dateParts = dateValue.substring(0, 10).split("-");
                        // Date constructor expects Month to be 0 based
                        date = new Date(+dateParts[0], +dateParts[1] - 1, +dateParts[2]);
                    }

                    //Omit time component of date and convert to milliseconds.
                    dates[i] = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
                }

                highchartsAxisOptions.tickPositioner = new AxisTickPositioner().getPositioner();

                //Any Values passing here are date typed objects, not untrusted strings.
                highchartsAxisOptions.categories = dates;
            }

            highchartsAxisOptions.labels.rotation = 0;
            highchartsAxisOptions.tickmarkPlacement = "on"; //Align tick with text
            highchartsAxisOptions.labels.formatter = new AxisMonthFormatter().getFormatter();
        } else if (axisOptions.labelFormatMode == Charts_Contracts.LabelFormatModes.NumberSeconds_HourMinuteSeconds) {
            highchartsAxisOptions.labels.formatter = TimeSpanFormatter.getFormatter();
        } else {
            if (axisOptions.labelValues) {
                //Note: We don't trust textual options from feature data - html encode the labels
                highchartsAxisOptions.categories = StringUtilities.encodeUntrustedStringArray(axisOptions.labelValues);
                highchartsAxisOptions.tickLength = 0; //Suppress ticks when axis is for categorical labels.
                highchartsAxisOptions.labels.formatter = TruncationFormatter.getFormatter(axisOptions.suppressLabelTruncation);
            } else {
                // We want to format the axis using the number in this case
                highchartsAxisOptions.labels.formatter = AbbreviatedNumberFormatter.getFormatter();
            }
        }

        if (axisOptions.allowDecimals != null) {
            highchartsAxisOptions.allowDecimals = axisOptions.allowDecimals;
        }
        if (axisOptions.plotLines != null) {
            highchartsAxisOptions.plotLines = this.translatePlotlines(axisOptions.plotLines);
        }
        if (axisOptions.plotBands != null) {
            highchartsAxisOptions.plotBands = this.translatePlotBands(axisOptions.plotBands);
        }

        return highchartsAxisOptions;
    }

    public translatePlotBands(bands: Charts_Contracts.AxisPlotBand[]): Highcharts.AxisPlotBandsOptions[] {
        return bands.map(band => {
            let item: Highcharts.AxisPlotBandsOptions = {
                color: band.color,
                from: band.start,
                to: band.end,
            };
            return item;
        });
    }

    public translatePlotlines(labelLines: Charts_Contracts.AxisPlotLine[]): Highcharts.AxisPlotLinesOptions[] {
        return labelLines.map(label => {
            const lineColor = label.lineColor || "#000000";
            const labelColor = label.labelColor || "#000000";
            let item: Highcharts.AxisPlotLinesOptions = {
                color: lineColor,
                label: {
                    text: label.labelText,
                    textAlign: "right",
                    x: 5,
                    y: -5,
                    rotation: 0,
                    style: {
                        color: labelColor
                    }
                },
                value: label.axisPosition,
                width: 2
            };
            return item;
        });
    }

    public onMouseClick(event: Highcharts.SeriesClickEventObject, isOtherTabClicked: boolean = false) {
        try {
            const eventPoint = event.point as HighchartsExtendedPointObject; // Point resides on point in series click event

            let label;
            if (this.getChartOptions().xAxis && this.getChartOptions().xAxis.labelFormatMode == Charts_Contracts.LabelFormatModes.DateTime_DayInMonth) {
                label = new Date(eventPoint.category);
            } else {
                label = eventPoint.category;
            }

            let seriesCustomData =
                eventPoint.series && event.point.series.options && (eventPoint.series.options as VssChartsSeriesOptions).customData
                    ? (eventPoint.series.options as VssChartsSeriesOptions).customData
                    : null;
            let seriesName = eventPoint.series != null ? eventPoint.series.name : null;

            //Note: The any casts are compensating for gaps in the Typescript wrappers.
            const clickEvent: Charts_Contracts.ClickEvent = {
                labelName: label,
                seriesName: seriesName,
                seriesCustomData: seriesCustomData,
                seriesDataIndex: eventPoint.index,
                itemX: eventPoint.x,
                itemY: eventPoint.y,
                itemZ: eventPoint.value, //TODO: Normalize our event handler contract to account for model differences.
                itemName: eventPoint.name,
                sourceChart: this,

                clientX: (<any>event).clientX,
                clientY: (<any>event).clientY,

                button: (<any>event).which,
                ctrlKey: (<any>event).ctrlKey,

                index: eventPoint.index,
                userClickedLabel: eventPoint.series?.userOptions?.name,
                isOtherTabClicked: isOtherTabClicked
            };

            // The chart is provided encoded names for security purposes.
            // We will decode them here when passing them back to the consumer.
            clickEvent.seriesName = StringUtilities.decodeString(clickEvent.seriesName);
            clickEvent.labelName = StringUtilities.decodeString(clickEvent.labelName);
            clickEvent.itemName = StringUtilities.decodeString(clickEvent.itemName);

            this.notifyClick(clickEvent);
        } catch (e) {
            console.error(e);
        }
    }

    /**
     * Handles Highcharts Point event click representation.
     * @param event
     */
    public onKeyClick(event: Highcharts.PointClickEventObject) {
        try {
            // TODO: CHECK THIS EVENT.TARGET THING!!!!
            let eventTarget = <any>event.target; //Point is stored as target on the point click event
            let seriesId = eventTarget.series.index;
            let series = this.getChartOptions().series[seriesId];

            var clickEvent: Charts_Contracts.ClickEvent = {
                sourceChart: this,
                itemX: eventTarget.x,
            };

            // Highcharts doesn't provide these fields with keyboard, currently. Once their accessibility input events are consistent, we'll expose them.
            clickEvent.labelName = undefined;
            clickEvent.itemName = undefined;
            clickEvent.clientX = undefined;
            clickEvent.clientY = undefined;
            clickEvent.button = undefined;
            clickEvent.ctrlKey = undefined;

            clickEvent.seriesName = series.name;
            clickEvent.seriesCustomData = series.customData;
            clickEvent.seriesDataIndex = eventTarget.index;
            clickEvent.itemY = eventTarget.y;

            // The chart is provided encoded names for security purposes.
            // We will decode them here when passing them back to the consumer.
            clickEvent.seriesName = StringUtilities.decodeString(clickEvent.seriesName);
            clickEvent.labelName = StringUtilities.decodeString(clickEvent.labelName);
            clickEvent.itemName = StringUtilities.decodeString(clickEvent.itemName);

            this.notifyClick(clickEvent);
        } catch (e) {
            // If a click handler blows up, note the problem for feature dev-follow up.
            console.error(e);
        }
    }

    public onLegendClick(event: Highcharts.SeriesLegendItemClickEventObject | Highcharts.PointLegendItemClickEventObject) {
        try {
            var legendClickEvent = <Charts_Contracts.LegendClickEvent>{
                // Add parameters here when needed
            };

            // If false is returned then Highcharts suppresses the default action which means it will not hide/show the series
            let allowDefault = this.notifyLegendClick(legendClickEvent);

            // Update y-axis min value when a legend item is shown/hidden
            if (this.getChartOptions().yAxis && this.getChartOptions().yAxis.updateMinOnLegendClick && allowDefault !== false) {
                let targetSeries: any = event.target;
                let targetIndex = targetSeries.index;

                // Because the target series hasn't been hidden/shown yet until after this method runs we check for the opposite value of its visible propery
                let visibleSeries = targetSeries.chart.series.filter(
                    (val, i) => (i === targetIndex && !val.visible) || (i !== targetIndex && val.visible)
                );

                if (visibleSeries.length > 0) {
                    let yVals = visibleSeries[0].yData;
                    targetSeries.yAxis.options.min = Math.min.apply(yVals, yVals);
                }
            }

            return allowDefault;
        } catch (e) {
            // If a click handler blows up, note the problem for feature dev-follow up.
            console.error(e);
        }
    }

    public notifyClick(clickEvent: Charts_Contracts.ClickEvent): void {
        if (typeof this.getChartOptions().click === "function") {
            this.getChartOptions().click(clickEvent);
        }
    }

    public notifyLegendClick(legendClickEvent: Charts_Contracts.LegendClickEvent) {
        if (typeof this.getChartOptions().legendClick === "function") {
            return this.getChartOptions().legendClick(legendClickEvent);
        }
    }

    // (Deprecated pattern) This should only be used for supporting stateful Click event handlers on a rendered chart.
    // Chart transform operations should all be pure functions.
    protected getChartOptions() {
        return this._chartOptions;
    }

    /**
     * Get zoom type options for the chart.
     * Returns x or y or xy or undefined, depending on what is specified in AxisOptions.canZoom
     * @param chartOptions
     */
    private getAxisZoomType(chartOptions: Charts_Contracts.ChartProps): "x" | "xy" | "y" | undefined {
        var zoomTypeX: "x" | undefined = chartOptions.xAxis && chartOptions.xAxis.canZoom === true ? "x" : undefined;
        var zoomTypeY: "y" | undefined = chartOptions.yAxis && chartOptions.yAxis.canZoom === true ? "y" : undefined;

        if (zoomTypeX !== undefined && zoomTypeY !== undefined) {
            return "xy";
        } else if (zoomTypeX !== undefined) {
            return zoomTypeX;
        } else if (zoomTypeY !== undefined) {
            return zoomTypeY;
        }

        return undefined;
    }
}

/** Placeholder for the Host to transport user-customizable coloring options. */
interface ColorCustomizationOptions {
    /** Opt-in to use subdued Coloring.
     Interpreted as false by default. */
    useSubduedPalette?: boolean;

    /** A list of user-customized colorings. */
    customColors?: Charts_Contracts.ColorEntry[];

    /** A rich color editing model. */
    colorDictionary?: Charts_Contracts.ColorDictionary;
}

class ChartColorizer {
    private colorOptions: ColorCustomizationOptions;
    private dictionary: Charts_Contracts.ColorDictionary;

    constructor(colorOptions: ColorCustomizationOptions) {
        this.colorOptions = colorOptions || {};
        if (this.colorOptions.colorDictionary) {
            this.dictionary = this.colorOptions.colorDictionary;
        } else {
            this.dictionary = new DefaultColorDictionary(this.colorOptions.customColors);
        }
    }

    /** Indicates if the chart uses a subdued color palette for readability.*/
    public isSubdued(): boolean {
        return this.colorOptions.useSubduedPalette == true;
    }

    /**
     * Retrieves a suitable color for the specified element
     * @param index - The index position of the element, within series or x-axis labels
     * @param name - The name of the element being colored.
     */
    public getColor(index: number, name: string): Charts_Contracts.ColorPair {
        return this.dictionary.getColorPair(name, index, this.isSubdued());
    }
}
